/**
 * SFrame224 — Residuary allocation. Persists to sframe-224.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useParams } from 'react-router-dom';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';

const step = SETTLEMENT_PHASE_CONFIGS.distribute.steps.find((s) => s.id === 'dist-2')!;

interface ResidBeneficiaryRow {
  name: string;
  percentage: number;
}

interface S224Persist {
  version: 1;
  beneficiaries: ResidBeneficiaryRow[];
  executorNotes: string;
}

function defaultPersist(): S224Persist {
  return { version: 1, beneficiaries: [{ name: '', percentage: 0 }], executorNotes: '' };
}

function coerce224(raw: unknown): S224Persist {
  const base = defaultPersist();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  let beneficiaries = base.beneficiaries;
  if (Array.isArray(o.beneficiaries)) {
    beneficiaries = (o.beneficiaries as unknown[])
      .map((x) => {
        if (!x || typeof x !== 'object') return { name: '', percentage: 0 };
        const b = x as Record<string, unknown>;
        const pct = typeof b.percentage === 'number' ? b.percentage : Number(b.percentage) || 0;
        const name = String(b.name ?? b.beneficiaryName ?? '').trim() || String(b.beneficiaryId ?? '').trim();
        return { name, percentage: pct };
      })
      .filter((row) => row.name.length > 0 || row.percentage > 0);
    if (!beneficiaries.length) beneficiaries = [{ name: '', percentage: 0 }];
  }
  return {
    version: 1,
    beneficiaries,
    executorNotes:
      typeof o.executorNotes === 'string' ? o.executorNotes : String(o.notes ?? ''),
  };
}

export default function SFrame224() {
  const { estateId: paramId } = useParams<{ estateId: string }>();
  const estateId = paramId ?? null;
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId, { readOnly: isArchived });

  const [data, setData] = useState<S224Persist>(() => defaultPersist());
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    setDataLoaded(false);
    if (!estateId) {
      setData(defaultPersist());
      setDataLoaded(true);
      return;
    }
    let c = false;
    (async () => {
      const fd = await loadFrameData('sframe-224');
      if (!c) {
        setData(coerce224(fd?.data));
        setDataLoaded(true);
      }
    })();
    return () => {
      c = true;
    };
  }, [estateId, loadFrameData]);

  useEffect(() => {
    if (!estateId || isArchived || !dataLoaded) return;
    const t = window.setTimeout(() => void saveFrameData('sframe-224', data as unknown as Record<string, unknown>), 520);
    return () => window.clearTimeout(t);
  }, [data, estateId, isArchived, dataLoaded, saveFrameData]);

  const totalPct = data.beneficiaries.reduce((s, b) => s + (Number.isFinite(b.percentage) ? b.percentage : 0), 0);
  const needsWarning = totalPct !== 100;

  const patchRows = useCallback(
    (rows: ResidBeneficiaryRow[]) => {
      if (isArchived) return;
      setData((prev) => ({ ...prev, beneficiaries: rows.length ? rows : [{ name: '', percentage: 0 }] }));
    },
    [isArchived]
  );

  const topContent = (
    <div
      className={`rounded-xl border px-4 py-3 text-sm mb-4 ${needsWarning ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}
    >
      <p className="font-semibold">Total: {totalPct}%</p>
      {needsWarning ? (
        <p className="mt-1">Total allocation must equal 100%. Current: {totalPct}%.</p>
      ) : (
        <p className="mt-1">Residual shares reconcile to exactly 100%.</p>
      )}
    </div>
  );

  return (
    <SettlementStepPage
      step={step}
      phase="distribute"
      overviewRoute="/sframe-220"
      plannerFrameIds={['frame-246']}
      settlementDocumentContext={{ phase: 'distribute', stepId: 'dist-2' }}
      statusControl="segmented"
      topContent={topContent}
    >
      <div className="space-y-6 mb-10">
        <p className="text-sm text-stone-600">Only mark this step complete when every residuary fractional share sums to precisely 100%.</p>
        <div className="flex justify-between gap-4 items-center">
          <h3 className="text-lg font-semibold text-blue-900">Beneficiary roster</h3>
          {!isArchived && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-lg border border-stone-300 px-3 py-1 text-sm hover:bg-stone-50"
              onClick={() => patchRows([...data.beneficiaries, { name: '', percentage: 0 }])}
            >
              <Plus className="w-4 h-4" /> Add beneficiary
            </button>
          )}
        </div>
        <div className="space-y-2">
          {data.beneficiaries.map((row, idx) => (
            <div key={idx} className="flex flex-wrap gap-2 items-start">
              <input
                className="flex-1 min-w-[180px] rounded-lg border border-stone-300 px-3 py-2 text-sm"
                placeholder="Beneficiary name"
                disabled={isArchived}
                value={row.name}
                onChange={(e) => {
                  const rows = [...data.beneficiaries];
                  rows[idx] = { ...row, name: e.target.value };
                  patchRows(rows);
                }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="w-24 rounded-lg border border-stone-300 px-2 py-2 text-sm"
                  disabled={isArchived}
                  value={Number.isFinite(row.percentage) ? row.percentage : ''}
                  onChange={(e) => {
                    const rows = [...data.beneficiaries];
                    rows[idx] = { ...row, percentage: Number(e.target.value) || 0 };
                    patchRows(rows);
                  }}
                />
                <span className="text-sm text-stone-600">%</span>
              </div>
              {!isArchived && data.beneficiaries.length > 1 && (
                <button
                  type="button"
                  aria-label="Remove"
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  onClick={() => patchRows(data.beneficiaries.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <label className="block">
          <span className="text-xs font-semibold text-stone-700 mb-1 block">Executor notes</span>
          <textarea
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm min-h-[96px]"
            disabled={isArchived}
            value={data.executorNotes}
            onChange={(e) => setData((prev) => ({ ...prev, executorNotes: e.target.value }))}
          />
        </label>
        {needsWarning && <p className="text-sm text-rose-800 font-medium">Do not toggle this step complete until percentages balance.</p>}
      </div>
    </SettlementStepPage>
  );
}